// ClientHome.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './clientHome.css';
import { DateTime } from 'luxon';
import ClientRequestsCard from './ClientRequests/ClientMyRequests';
import useClientRequestsEnabled from './ClientRequests/useClientRequestsEnabled';
import ClientPendingInvoicesCard from './ClientRequests/ClientPendingInvoicesCard';
import WalletCommitted from './WalletCommitted';
// amountOwed / amountOwedCount / isInDebt are no longer imported here: the
// owed figure left this page's stat grid (see the note beside the wallet stat).
// They remain the shared definition of "money owed" for the surfaces that still
// render it — do not re-derive owed money locally anywhere.
import { displayAvailable, isOvercommitted } from './walletFigures';

function ClientHome() {
  const [firstName, setFirstName] = useState('');
  const [summary, setSummary] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [myDeposits, setMyDeposits] = useState([]);
  // Seeds for the two self-fetching child cards, loaded with the page below so
  // they paint in the same frame as the rest of the dashboard (no late pop-in).
  const [pendingInvoiceTotal, setPendingInvoiceTotal] = useState(0);
  // Separate entity wallets: each entity wallet's own pending pool.
  const [pendingWallets, setPendingWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const requestsEnabled = useClientRequestsEnabled();

  // Refresh just the "Your Requests" card (e.g. after the client cancels one).
  // The initial load is gated with the rest of the page below; this is only for
  // user-initiated updates after mount. 503 = feature off → treat as empty.
  const reloadMyRequests = useCallback(async () => {
    try {
      const res = await axios.get('/api/client-requests/requests/my');
      setMyRequests(Array.isArray(res.data?.requests) ? res.data.requests : []);
    } catch (err) {
      // 503 = feature flag flipped off mid-session → hide the card, matching the
      // initial-load branch. Other errors: keep whatever we last had, just log.
      if (err.response?.status === 503) setMyRequests([]);
      else console.error('Error reloading client requests:', err);
    }
    // Withdrawing/submitting a request changes reserved & available balance, so
    // refresh the wallet snapshot the stat card and footer render from too.
    try {
      const wRes = await axios.get('/api/client-requests/wallet');
      setWalletBalance(wRes.data);
    } catch (err) {
      if (err.response?.status !== 503) console.error('Error reloading wallet:', err);
    }
    // Price adjustments RETIRED (2026-07-21) — no adjustment reload here.
  }, []);

  useEffect(() => {
    const storedFirstName = localStorage.getItem('firstName') || '';
    const userRole = localStorage.getItem('userRole');
    setFirstName(storedFirstName);

    if (userRole !== 'client') {
      navigate('/');
      return;
    }

    // Fire every home-page request in parallel but only drop the loading overlay
    // once ALL have settled — so the whole dashboard (wallet card AND the "Your
    // Requests" card) paints at once, instead of a card popping in a beat later.
    (async () => {
      const [summaryRes, walletRes, requestsRes, depositsRes, pendingInvoiceRes] = await Promise.allSettled([
        axios.get('/api/client-requests/properties/summary'),
        axios.get('/api/client-requests/wallet'),
        // Secondary cards — bound them so a slow/hung call can't pin the whole
        // dashboard behind the spinner; on timeout they degrade to no card.
        axios.get('/api/client-requests/requests/my', { timeout: 10000 }),
        axios.get('/api/client-requests/wallet/deposits', { timeout: 10000 }),
        // Seed for the pending-invoices child card, fetched here so it paints
        // in-frame instead of self-fetching a beat later.
        axios.get('/api/client-requests/invoices/pending-total', { timeout: 10000 }),
      ]);
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data);
      else console.error('Error fetching client summary:', summaryRes.reason);
      if (walletRes.status === 'fulfilled') setWalletBalance(walletRes.value.data);
      else console.error('Error fetching wallet balance:', walletRes.reason);
      if (requestsRes.status === 'fulfilled') {
        setMyRequests(Array.isArray(requestsRes.value.data?.requests) ? requestsRes.value.data.requests : []);
      } else if (requestsRes.reason?.response?.status !== 503) {
        // 503 = feature flag off → no card. Anything else is a real error.
        console.error('Error fetching client requests:', requestsRes.reason);
      }
      if (depositsRes.status === 'fulfilled') {
        const rows = Array.isArray(depositsRes.value.data) ? depositsRes.value.data : [];
        setMyDeposits(rows.filter((d) => d.status === 'pending'));
      } else {
        console.error('Error fetching deposits:', depositsRes.reason);
      }
      if (pendingInvoiceRes.status === 'fulfilled') {
        setPendingInvoiceTotal(Number(pendingInvoiceRes.value.data?.pending_total || 0));
        setPendingWallets(Array.isArray(pendingInvoiceRes.value.data?.wallets) ? pendingInvoiceRes.value.data.wallets : []);
      } else {
        // 404/503 = feature not deployed/off → leave the default 0 (card hides).
        const st = pendingInvoiceRes.reason?.response?.status;
        if (st !== 404 && st !== 503) console.error('Error fetching pending invoices total:', pendingInvoiceRes.reason);
      }
      setLoading(false);
    })();
  }, [navigate]);

  const handleBookingClick = (id) => navigate(`/client/bookings/${id}`);

  const formatDate = (dateString) => {
    const dubaiTime = DateTime.fromISO(dateString, { zone: 'Asia/Dubai' });
    return dubaiTime.toFormat('dd LLL yyyy • HH:mm');
  };

  const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const safe = useMemo(() => {
    return {
      properties: summary?.properties || 0,
      futureBookings: summary?.futureBookings || 0,
      pastBookings: summary?.pastBookings || 0,
      nextFutureBookings: summary?.nextFutureBookings || [],
      lastPastBookings: summary?.lastPastBookings || [],
      ongoingBookingsList: summary?.ongoingBookingsList || [],
    };
  }, [summary]);

  // Getting-started checklist (PLAN_registration.md Phase 1): shown to fresh
  // accounts until they own the two firsts. A PENDING create_property /
  // create_booking request already counts — a request awaiting review is
  // progress, not a locked gate.
  const onboarding = useMemo(() => {
    const reqs = Array.isArray(myRequests) ? myRequests : [];
    const hasPendingOfType = (t) => reqs.some((r) => r.request_type === t && r.status === 'pending');
    const propertyDone = safe.properties > 0 || hasPendingOfType('create_property');
    const bookingDone =
      safe.futureBookings > 0 || safe.pastBookings > 0 ||
      safe.ongoingBookingsList.length > 0 || hasPendingOfType('create_booking');
    return { propertyDone, bookingDone, show: !(propertyDone && bookingDone) };
  }, [safe, myRequests]);

  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-spinner" />
        <div className="app-spinner-text">Loading Your Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="chome">
      <div className="chome__header">
        <div className="chome__headerInner">
          <h1 className="chome__title">
            Welcome <span className="chome__name">{firstName || 'Client'}</span> 👋
          </h1>
          <p className="chome__sub">Here’s a quick summary of your account.</p>
        </div>
      </div>

      <div className="chome__container">
        {/* Getting started — fresh accounts see their two firsts as steps;
            booking stays locked until a property exists (even as a pending
            request). Disappears forever once both are done. */}
        {onboarding.show && (
          <div className="chomeOnb">
            <div className="chomeOnb__head">
              <h3 className="chomeOnb__title">Let's get you set up</h3>
              <span className="chomeOnb__progress">
                {(onboarding.propertyDone ? 1 : 0) + (onboarding.bookingDone ? 1 : 0)}/2 done
              </span>
            </div>
            <div className="chomeOnb__steps">
              <Link
                to="/client/properties"
                className={`chomeOnb__step ${onboarding.propertyDone ? 'is-done' : ''}`}
              >
                <span className="chomeOnb__tick">{onboarding.propertyDone ? '✓' : '1'}</span>
                <span className="chomeOnb__stepBody">
                  <span className="chomeOnb__stepTitle">Add your first property</span>
                  <span className="chomeOnb__stepSub">
                    {onboarding.propertyDone
                      ? 'Done — your property is in.'
                      : 'Tell us where we’ll be cleaning.'}
                  </span>
                </span>
                {!onboarding.propertyDone && <span className="chomeOnb__go">Start →</span>}
              </Link>

              {onboarding.propertyDone ? (
                <Link
                  to="/client/bookings/request"
                  className={`chomeOnb__step ${onboarding.bookingDone ? 'is-done' : ''}`}
                >
                  <span className="chomeOnb__tick">{onboarding.bookingDone ? '✓' : '2'}</span>
                  <span className="chomeOnb__stepBody">
                    <span className="chomeOnb__stepTitle">Request your first booking</span>
                    <span className="chomeOnb__stepSub">
                      {onboarding.bookingDone
                        ? 'Done — we’ll take it from here.'
                        : 'Pick a date and we’ll confirm it.'}
                    </span>
                  </span>
                  {!onboarding.bookingDone && <span className="chomeOnb__go">Start →</span>}
                </Link>
              ) : (
                <div className="chomeOnb__step is-locked" aria-disabled="true">
                  <span className="chomeOnb__tick">🔒</span>
                  <span className="chomeOnb__stepBody">
                    <span className="chomeOnb__stepTitle">Request your first booking</span>
                    <span className="chomeOnb__stepSub">Unlocks once your property is added.</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stat cards */}
        <div className="chome__stats">
          <Link className="chomeStat" to="/client/properties">
            <div className="chomeStat__top">
              <div className="chomeStat__label">Properties</div>
              <div className="chomeStat__icon">🏠</div>
            </div>
            <div className="chomeStat__value">{safe.properties}</div>
            <div className="chomeStat__hint">View your properties</div>
          </Link>

          <Link className="chomeStat" to="/client/bookings?view=future">
            <div className="chomeStat__top">
              <div className="chomeStat__label">Upcoming Bookings</div>
              <div className="chomeStat__icon">📅</div>
            </div>
            <div className="chomeStat__value">{safe.futureBookings}</div>
            <div className="chomeStat__hint">See what’s next</div>
          </Link>

          <Link className="chomeStat" to="/client/bookings?view=past">
            <div className="chomeStat__top">
              <div className="chomeStat__label">Past Bookings</div>
              <div className="chomeStat__icon">📅</div>
            </div>
            <div className="chomeStat__value">{safe.pastBookings}</div>
            <div className="chomeStat__hint">View history</div>
          </Link>

          {/* AVAILABLE is the headline (2026-07-18). This card used to show
              total_balance while the wallet page showed available — so a client
              whose home said 8,000 and whose wallet said 2,400 was looking at
              the same wallet, and the home number was the one they remembered.
              A client must never see a headline bigger than they can spend.
              Total + every committed line live in the expandable detail. */}
          {walletBalance && (
            <Link className="chomeStat" to="/client/wallet">
              <div className="chomeStat__top">
                <div className="chomeStat__label">Available to spend</div>
                <div className="chomeStat__icon">💳</div>
              </div>
              <div className={`chomeStat__value ${isOvercommitted(walletBalance) ? 'chomeStat__value--short' : ''}`}>
                AED {fmtMoney(displayAvailable(walletBalance))}
              </div>
              {isOvercommitted(walletBalance) ? (
                /* HONEST NEGATIVE (2026-07-18): the headline carries the
                   real signed figure rather than a floored AED 0.00. The long
                   explanatory sentence was dropped (2026-07-19) — the
                   committed amounts are already listed beside this card, so it
                   only restated them. A short cue keeps the negative from
                   reading as an error. */
                <div className="chomeStat__hint chomeStat__hint--reserved">
                  Committed to pending requests
                </div>
              ) : Number(walletBalance.pending_deposits_total) > 0 ? (
                <div className="chomeStat__hint chomeStat__hint--incoming">
                  +AED {fmtMoney(walletBalance.pending_deposits_total)} deposit pending
                </div>
              ) : (
                <div className="chomeStat__hint">Manage your wallet</div>
              )}
              <WalletCommitted wallet={walletBalance} />
            </Link>
          )}

          {/* AMOUNT OWED — REMOVED from the stat grid (2026-07-19). The
              money the client owes is not hidden; it is stated TWICE further
              down this same page, in the two places that can also act on it:
              <ClientPendingInvoicesCard> (pending invoices, with a settle CTA)
              and the price-adjustments card (each adjustment, reviewable and
              payable). A third headline figure up here only duplicated them,
              and a duplicated total is the one that goes stale.

              The rule this does NOT change: owed money is still kept OUT of the
              wallet headline (walletFigures.availableBalance) and out of the
              committed lines. Nothing here nets a debt into a balance — the
              debt simply lives in the sections that own it. */}

          {/* QUICK ACTIONS as the FIFTH column (2026-07-23) — the two
              things a client comes here to do, sharing the stat row instead of
              floating under it. Collapses with the cards on small screens. */}
          {requestsEnabled && (
            <div className="chomeStat--actions">
              <button
                type="button"
                className="chomeAction chomeAction--primary"
                onClick={() => navigate('/client/bookings/request')}
              >
                Request a booking
              </button>
              <button
                type="button"
                className="chomeAction"
                onClick={() => navigate('/client/properties?new=1')}
              >
                Add a property
              </button>
            </div>
          )}
        </div>

        {/* Ongoing — full-width, above the Next/Last grid, only when active */}
        {safe.ongoingBookingsList.length > 0 && (
          <div className="chomeCard chomeCard--wide chomeCard--ongoing">
            <div className="chomeCard__head">
              <div>
                <h3 className="chomeCard__title">
                  <span className="chomeCard__ongoingDot" aria-hidden="true" /> Ongoing Bookings
                </h3>
                <div className="chomeCard__sub">Cleanings currently in progress</div>
              </div>

              <Link className="chomeCard__action" to="/client/bookings?view=ongoing">
                View all →
              </Link>
            </div>

            <div className="chomeList">
              {safe.ongoingBookingsList.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="chomeItem"
                  onClick={() => handleBookingClick(b.id)}
                >
                  <div className="chomeItem__left">
                    <div className="chomeItem__date">{formatDate(b.date)}</div>
                    <div className="chomeItem__meta">
                      {b.unit ? <span className="chomeItem__building">Unit {b.unit}</span> : null}
                      {b.unit ? <span className="chomeItem__dot">·</span> : null}
                      <span className="chomeItem__building">{b.building}</span>
                    </div>
                  </div>
                  <div className="chomeItem__chev">›</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pending invoices — prominent settle CTA (2026-07-16). Nothing due → hidden.
            Seeded with the page load so it paints in-frame instead of self-fetching. */}
        <ClientPendingInvoicesCard total={pendingInvoiceTotal} wallets={pendingWallets} />

        {/* Price adjustments RETIRED (2026-07-21) — a booking's unpaid
            change now shows as its pending amount + price-change history on the
            booking, not a Home card. */}

        {/* Pending/recent requests + pending deposits — below Ongoing.
            Data is fetched with the page (above) so it paints in the same frame. */}
        <ClientRequestsCard requests={myRequests} deposits={myDeposits} onReload={reloadMyRequests} />

        {/* Two cards: Next + Last */}
        <div className="chome__grid">
          <div className="chomeCard">
            <div className="chomeCard__head">
              <div>
                <h3 className="chomeCard__title">Next 5 Bookings</h3>
                <div className="chomeCard__sub">Upcoming scheduled cleanings</div>
              </div>

              <Link className="chomeCard__action" to="/client/bookings?view=future">
                View all →
              </Link>
            </div>

            <div className="chomeList">
              {safe.nextFutureBookings.length > 0 ? (
                safe.nextFutureBookings.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className="chomeItem"
                    onClick={() => handleBookingClick(b.id)}
                  >
                    <div className="chomeItem__left">
                      <div className="chomeItem__date">{formatDate(b.date)}</div>
                      <div className="chomeItem__meta">
                        {b.unit ? <span className="chomeItem__building">Unit {b.unit}</span> : null}
                        {b.unit ? <span className="chomeItem__dot">·</span> : null}
                        <span className="chomeItem__building">{b.building}</span>
                      </div>
                    </div>
                    <div className="chomeItem__chev">›</div>
                  </button>
                ))
              ) : (
                <div className="chomeEmpty">
                  <div className="chomeEmpty__icon">✨</div>
                  <div className="chomeEmpty__title">No upcoming bookings</div>
                  <div className="chomeEmpty__text">When a booking is scheduled, it will appear here.</div>
                </div>
              )}
            </div>
          </div>

          <div className="chomeCard">
            <div className="chomeCard__head">
              <div>
                <h3 className="chomeCard__title">Last 5 Bookings</h3>
                <div className="chomeCard__sub">Your most recent completed visits</div>
              </div>

              <Link className="chomeCard__action" to="/client/bookings?view=past">
                View all →
              </Link>
            </div>

            <div className="chomeList">
              {safe.lastPastBookings.length > 0 ? (
                safe.lastPastBookings.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className="chomeItem"
                    onClick={() => handleBookingClick(b.id)}
                  >
                    <div className="chomeItem__left">
                      <div className="chomeItem__date">{formatDate(b.date)}</div>
                      <div className="chomeItem__meta">
                        {b.unit ? <span className="chomeItem__building">Unit {b.unit}</span> : null}
                        {b.unit ? <span className="chomeItem__dot">·</span> : null}
                        <span className="chomeItem__building">{b.building}</span>
                      </div>
                    </div>
                    <div className="chomeItem__chev">›</div>
                  </button>
                ))
              ) : (
                <div className="chomeEmpty">
                  <div className="chomeEmpty__icon">🧼</div>
                  <div className="chomeEmpty__title">No past bookings yet</div>
                  <div className="chomeEmpty__text">Once we complete a booking, it will show up here.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientHome;
